/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
/* eslint-disable @typescript-eslint/no-unused-vars */
import Injectable from '../../core/Injectable.js';
import { CommandBus } from '../../core/command/CommandBus.js';
import { ConnectCommand } from '../../app/usecase/command/network/connect/ConnectCommand.js';
import ConnectRequest, { SupportedWallets, } from './request/ConnectRequest.js';
import RequestMapper from './request/mapping/RequestMapper.js';
import TransactionService from '../../app/service/TransactionService.js';
import NetworkService from '../../app/service/NetworkService.js';
import SetNetworkRequest from './request/SetNetworkRequest.js';
import { SetNetworkCommand } from '../../app/usecase/command/network/setNetwork/SetNetworkCommand.js';
import { SetConfigurationCommand } from '../../app/usecase/command/network/setConfiguration/SetConfigurationCommand.js';
import { unrecognized, } from '../../domain/context/network/Environment.js';
import InitializationRequest from './request/InitializationRequest.js';
import Event from './Event.js';
import RPCTransactionAdapter from '../out/rpc/RPCTransactionAdapter.js';
import { HashpackTransactionAdapter } from '../out/hs/hashpack/HashpackTransactionAdapter.js';
import { LogError } from '../../core/decorator/LogErrorDecorator.js';
import SetConfigurationRequest from './request/SetConfigurationRequest.js';
import { handleValidation } from './Common.js';
import { BladeTransactionAdapter } from '../out/hs/blade/BladeTransactionAdapter.js';
import { FireblocksTransactionAdapter } from '../out/hs/hts/custodial/FireblocksTransactionAdapter.js';
import { DFNSTransactionAdapter } from '../out/hs/hts/custodial/DFNSTransactionAdapter.js';
import { MultiSigTransactionAdapter } from '../out/hs/multiSig/MultiSigTransactionAdapter.js';
import SetBackendRequest from './request/SetBackendRequest.js';
import { SetBackendCommand } from '../../app/usecase/command/network/setBackend/SetBackendCommand.js';
import BackendEndpoint from '../../domain/context/network/BackendEndpoint.js';
export { SupportedWallets };
class NetworkInPort {
    constructor(commandBus = Injectable.resolve(CommandBus), transactionService = Injectable.resolve(TransactionService), networkService = Injectable.resolve(NetworkService)) {
        this.commandBus = commandBus;
        this.transactionService = transactionService;
        this.networkService = networkService;
    }
    async setConfig(req) {
        handleValidation('SetConfigurationRequest', req);
        const res = await this.commandBus.execute(new SetConfigurationCommand(req.factoryAddress));
        return res;
    }
    getFactoryAddress() {
        return this.networkService.configuration
            ? this.networkService.configuration.factoryAddress
            : '';
    }
    getNetwork() {
        return this.networkService.environment;
    }
    isNetworkRecognized() {
        return this.networkService.environment != unrecognized;
    }
    async setNetwork(req) {
        handleValidation('SetNetworkRequest', req);
        const res = await this.commandBus.execute(new SetNetworkCommand(req.environment, req.mirrorNode, req.rpcNode, req.consensusNodes));
        return res;
    }
    async init(req) {
        handleValidation('InitializationRequest', req);
        await this.setNetwork(new SetNetworkRequest({
            environment: req.network,
            mirrorNode: req.mirrorNode,
            rpcNode: req.rpcNode,
            consensusNodes: req.consensusNodes,
        }));
        if (req.configuration)
            if (req.configuration.factoryAddress)
                await this.setConfig(new SetConfigurationRequest({
                    factoryAddress: req.configuration.factoryAddress,
                }));
        if (req.backend)
            await this.setBackend(new SetBackendRequest({ url: req.backend.url }));
        req.events && Event.register(req.events);
        const wallets = [];
        const instances = Injectable.registerTransactionAdapterInstances();
        for (const val of instances) {
            if (val instanceof RPCTransactionAdapter) {
                wallets.push(SupportedWallets.METAMASK);
            }
            else if (val instanceof HashpackTransactionAdapter) {
                wallets.push(SupportedWallets.HASHPACK);
            }
            else if (val instanceof BladeTransactionAdapter) {
                wallets.push(SupportedWallets.BLADE);
            }
            else if (val instanceof FireblocksTransactionAdapter) {
                wallets.push(SupportedWallets.FIREBLOCKS);
            }
            else if (val instanceof DFNSTransactionAdapter) {
                wallets.push(SupportedWallets.DFNS);
            }
            else if (val instanceof MultiSigTransactionAdapter) {
                wallets.push(SupportedWallets.MULTISIG);
            }
            else {
                wallets.push(SupportedWallets.CLIENT);
            }
            await val.init();
            if (val instanceof RPCTransactionAdapter) {
                val.setMirrorNodes(req.mirrorNodes);
                val.setJsonRpcRelays(req.jsonRpcRelays);
                val.setFactories(req.factories);
            }
        }
        return wallets;
    }
    async connect(req) {
        handleValidation('ConnectRequest', req);
        console.log('connect');
        const account = req.account
            ? RequestMapper.mapAccount(req.account)
            : undefined;
        const custodialSettings = this.getCustodialSettings(req);
        if (req.wallet == SupportedWallets.HASHPACK ||
            req.wallet == SupportedWallets.BLADE) {
            const instances = Injectable.registerTransactionAdapterInstances();
            for (const val of instances) {
                if (val instanceof HashpackTransactionAdapter ||
                    val instanceof BladeTransactionAdapter) {
                    await val.restart(req.network);
                }
            }
        }
        console.log('SetNetworkCommand', req.network, req.mirrorNode, req.rpcNode);
        await this.commandBus.execute(new SetNetworkCommand(req.network, req.mirrorNode, req.rpcNode, req.consensusNodes));
        console.log('ConnectRequest', req.wallet, account, custodialSettings);
        const res = await this.commandBus.execute(new ConnectCommand(req.network, req.wallet, account, custodialSettings));
        return res.payload;
    }
    async setBackend(req) {
        handleValidation('SetBackendRequest', req);
        const be = new BackendEndpoint(req.url);
        const res = await this.commandBus.execute(new SetBackendCommand(be));
        return res.backendEndpoint;
    }
    getCustodialSettings(req) {
        if (req.custodialWalletSettings &&
            req.wallet === SupportedWallets.DFNS) {
            return RequestMapper.dfnsRequestToDfnsSettings(req.custodialWalletSettings);
        }
        else if (req.custodialWalletSettings &&
            req.wallet === SupportedWallets.FIREBLOCKS) {
            return RequestMapper.fireblocksRequestToFireblocksSettings(req.custodialWalletSettings);
        }
        return undefined;
    }
    disconnect() {
        return this.transactionService.getHandler().stop();
    }
}
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [SetConfigurationRequest]),
    __metadata("design:returntype", Promise)
], NetworkInPort.prototype, "setConfig", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", String)
], NetworkInPort.prototype, "getFactoryAddress", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", String)
], NetworkInPort.prototype, "getNetwork", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Boolean)
], NetworkInPort.prototype, "isNetworkRecognized", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [SetNetworkRequest]),
    __metadata("design:returntype", Promise)
], NetworkInPort.prototype, "setNetwork", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [InitializationRequest]),
    __metadata("design:returntype", Promise)
], NetworkInPort.prototype, "init", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [ConnectRequest]),
    __metadata("design:returntype", Promise)
], NetworkInPort.prototype, "connect", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [SetBackendRequest]),
    __metadata("design:returntype", Promise)
], NetworkInPort.prototype, "setBackend", null);
const Network = new NetworkInPort();
export default Network;
